import { computed, Signal } from '@angular/core';
import { DomainType } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';

/**
 * Canonical `$ctx` for manage surfaces.
 *
 * `$ctx` is the per-mount session context rule conditions gate on via the `$ctx.` bind prefix
 * (see WbSurfaceDefinition.context). It lives OUTSIDE the model: read-only for rules, never
 * persisted, never emitted on a change stream. The manage block fills `domain` and `flags`
 * generically; entities supply their own `rights` map (the only entity-specific slice).
 *
 * ── Canonical key vocabulary (CONTRACT — schemas author rules against these paths) ────────────
 *
 *   $ctx.domain                       DomainType of the current session ('Tenant' | 'ClientPortal'
 *                                     | 'ContractorPortal' | 'Mobile' | …). Rules gate portal-only
 *                                     or tenant-only behaviour on this. Example:
 *                                       c.eq('$ctx.domain', 'Tenant')
 *
 *   $ctx.flags.<featureFlag>          Manage-relevant feature flags, boolean. Reserved namespace for
 *                                     flags that toggle manage behaviour (e.g. beta surfaces). Filled
 *                                     generically by the block; empty object today, extended here (not
 *                                     per-entity) as manage-scoped flags are introduced. Example:
 *                                       c.eq('$ctx.flags.newActivityEditor', true)
 *
 *   $ctx.rights.<domain>.<capability> Entity-supplied rights map, boolean leaves. Entities pass their
 *                                     own resolved RightsService booleans keyed by their own domain
 *                                     grouping (e.g. rights.crm.manageSites, rights.sales.manageLeads).
 *                                     The block does NOT invent right keys — it forwards what the
 *                                     entity computed. Example:
 *                                       c.eq('$ctx.rights.sales.manageLeads', true)
 *
 * Keep these three top-level keys stable: `domain`, `flags`, `rights`. Adding a new top-level key is
 * a contract change — document it here first.
 */
export interface ManageSurfaceContext {
  readonly domain: DomainType;
  readonly flags: Readonly<Record<string, boolean>>;
  readonly rights: Readonly<Record<string, unknown>>;
}

/** Optional inputs an entity contributes to its manage `$ctx`. Domain/flags are filled generically. */
export interface CreateManageSurfaceContextArgs {
  /** Entity-specific rights map (booleans, possibly nested by domain grouping). Forwarded verbatim
   *  under `$ctx.rights`. */
  readonly rights?: Readonly<Record<string, unknown>>;
  /** Manage-relevant feature flags to merge over the block-provided defaults (rare — most flags
   *  belong to the block, not one entity). */
  readonly flags?: Readonly<Record<string, boolean>>;
}

/**
 * Builds the canonical manage `$ctx` as a computed signal. Reactive so rights/flags that resolve
 * after login (or a "view-as" switch) re-publish into the surface and dependent rules re-evaluate.
 */
export function createManageSurfaceContext(args?: CreateManageSurfaceContextArgs): Signal<ManageSurfaceContext> {

  const domain = (ConfigService.environment.domainType as DomainType) ?? DomainType.Tenant;

  return computed<ManageSurfaceContext>(() => ({
    domain,
    flags: { ...(args?.flags ?? {}) },
    rights: { ...(args?.rights ?? {}) },
  }));
}
